<template>
  <div class="ap" dir="rtl">
    <!-- ============ صفحهٔ ورود (توکن) ============ -->
    <div v-if="!authed" class="ap-login-wrap">
      <form class="ap-login" @submit.prevent="login">
        <div class="ap-login-icon">🔐</div>
        <h1>پنل مدیریت ضربه آزاد</h1>
        <p class="ap-login-hint">توکن مدیریتی (ADMIN_TOKEN) را وارد کنید — در فایل .env سرور تعریف شده است.</p>
        <input
          v-model="tokenInput"
          type="password"
          placeholder="توکن مدیریتی"
          autocomplete="current-password"
          required
        >
        <button type="submit" :disabled="loginBusy">{{ loginBusy ? 'در حال بررسی…' : 'ورود' }}</button>
        <p v-if="loginError" class="ap-err">{{ loginError }}</p>
        <p v-if="loginNoServerToken" class="ap-warn">
          سرور هنوز ADMIN_TOKEN ندارد. در cPanel → Terminal فقط یک خط:
          <code>bash ~/backend/deploy/set-admin-token.sh</code>
          — توکن چاپ‌شده را همین‌جا وارد کنید.
        </p>
      </form>
    </div>

    <!-- ============ پنل ============ -->
    <template v-else>
      <header class="ap-head">
        <div class="ap-head-in">
          <h1>پنل مدیریت <span>ضربه آزاد</span></h1>
          <div class="ap-head-actions">
            <a href="/" target="_blank" rel="noopener" class="ap-ghost">مشاهده سایت ↗</a>
            <button class="ap-ghost" @click="logout">خروج</button>
          </div>
        </div>
        <nav class="ap-tabs">
          <button
            v-for="t in tabs"
            :key="t.id"
            :class="['ap-tab', activeTab === t.id ? 'on' : '']"
            @click="activeTab = t.id"
          >{{ t.icon }} {{ t.name }}</button>
        </nav>
      </header>

      <main class="ap-body">
        <div v-if="flash" :class="['ap-flash', flashType]">{{ flash }}</div>

        <!-- ============ داشبورد ============ -->
        <section v-if="activeTab === 'dashboard'">
          <div class="ap-cards">
            <div class="ap-card"><b>{{ stats.newsCount || 0 }}</b><span>خبر در دیتابیس</span></div>
            <div class="ap-card"><b>{{ stats.factsPublished || 0 }}</b><span>فکت منتشرشده</span></div>
            <div class="ap-card"><b>{{ stats.matchesCount || 0 }}</b><span>مسابقه</span></div>
            <div class="ap-card"><b>{{ stats.teamsCount || 0 }}</b><span>تیم</span></div>
            <div class="ap-card"><b>{{ stats.eventsActive || 0 }}</b><span>رویداد فعال</span></div>
            <div class="ap-card"><b>{{ stats.factsCount || 0 }}</b><span>کل فکت‌ها (با پیش‌نویس)</span></div>
          </div>
          <div class="ap-note">
            <p v-if="stats.lastNewsAt">آخرین خبر ثبت‌شده: {{ fmtDate(stats.lastNewsAt) }}</p>
            <p v-else>هنوز خبری ثبت نشده است.</p>
            <p>دانستنی‌های منتشرشده در <a href="/did-you-know/" target="_blank" rel="noopener">صفحهٔ دانستنی‌ها ↗</a> نمایش داده می‌شوند؛ بعد از هر تغییر، پیش‌رندر ساعتی صفحه را بروز می‌کند.</p>
          </div>
        </section>

        <!-- ============ اخبار ============ -->
        <section v-if="activeTab === 'news'">
          <div class="ap-sec-head">
            <h2>مدیریت اخبار <small>({{ newsList.length }} مورد اخیر)</small></h2>
            <button class="ap-btn" @click="openNewsForm()">＋ خبر جدید</button>
          </div>

          <form v-if="showNewsForm" class="ap-form" @submit.prevent="saveNews">
            <h3>{{ editNewsId ? 'ویرایش خبر' : 'افزودن خبر' }}</h3>
            <label>عنوان *<input v-model="newsForm.title" required></label>
            <label>خلاصه<textarea v-model="newsForm.summary" rows="2"></textarea></label>
            <label>متن کامل<textarea v-model="newsForm.content" rows="6"></textarea></label>
            <label>آدرس عکس<input v-model="newsForm.imageUrl" placeholder="https://…"></label>
            <div class="ap-form-actions">
              <button type="submit" class="ap-btn" :disabled="busy">ذخیره</button>
              <button type="button" class="ap-btn ghost" @click="closeNewsForm">انصراف</button>
            </div>
          </form>

          <div class="ap-list">
            <div v-for="n in newsList" :key="n.id" class="ap-row">
              <div class="ap-row-main">
                <h4>{{ n.title }}</h4>
                <p class="ap-muted">
                  {{ (n.source && n.source.name) || '—' }} • {{ fmtDate(n.publishedAt || n.createdAt) }}
                  • {{ n.views || 0 }} بازدید
                </p>
              </div>
              <div class="ap-row-actions">
                <a v-if="n.slug" :href="'/news/' + n.slug + '/'" target="_blank" rel="noopener" class="ap-mini">دیدن ↗</a>
                <button class="ap-mini" @click="openNewsForm(n)">ویرایش</button>
                <button class="ap-mini danger" @click="removeNews(n)">حذف</button>
              </div>
            </div>
            <p v-if="!newsList.length" class="ap-empty">خبری نیست.</p>
          </div>
        </section>

        <!-- ============ دانستنی‌ها ============ -->
        <section v-if="activeTab === 'facts'">
          <div class="ap-sec-head">
            <h2>دانستنی‌های فوتبالی <small>({{ factsList.length }} فکت)</small></h2>
            <button class="ap-btn" @click="openFactForm()">＋ فکت جدید</button>
          </div>

          <!-- «دانستنیِ روز» — خط لولهٔ خودکار -->
          <div class="ap-daily">
            <div class="ap-daily-status">
              <b>⭐ دانستنیِ روز:</b>
              <span v-if="dailyStatus && dailyStatus.todaysFact">
                «{{ dailyStatus.todaysFact.title }}»
                <a :href="'/did-you-know/' + encodeURIComponent(dailyStatus.todaysFact.slug) + '/'" target="_blank" rel="noopener">مشاهده ↗</a>
              </span>
              <span v-else-if="dailyStatus">هنوز امروز منتشر نشده — زمان‌بند خودکار ساعت ۰۶:۴۰ تهران کارش را می‌کند</span>
            </div>
            <div class="ap-daily-actions">
              <button class="ap-mini" :disabled="dailyBusy" @click="runDaily()">اجرای خط لولهٔ امروز</button>
              <button class="ap-mini ghost" :disabled="dailyBusy" @click="runDaily(true)">جایگزینی با مطلب تازه</button>
              <button class="ap-mini ghost" :disabled="dailyBusy" @click="previewDaily()">پیش‌نمایش (بدون انتشار)</button>
            </div>
            <p v-if="dailyBusy" class="ap-muted">در حال اجرا — اسکن منابع بین‌المللی و بازنویسی هوش مصنوعی ممکن است ۱ تا ۳ دقیقه طول بکشد…</p>
            <pre v-if="dailyResult" class="ap-daily-result">{{ dailyResult }}</pre>
            <p class="ap-muted ap-daily-hint">
              خط لولهٔ خودکار روزی یک مطلب کامل از ۴ منبع بین‌المللی آمار/رکورد می‌سازد (FourFourTwo، Opta Analyst، MyFootballFacts، RSSSF)، مقاله را به فارسی بازنویسی می‌کند (هوش مصنوعی + فالبک قالبی) و با عکس مرتبط منتشر می‌کند — عکس نبود، تصویر مفهومی هوش مصنوعی/ویکی‌مدیا. منبع اصلی همیشه لینک می‌شود.
            </p>
          </div>

          <form v-if="showFactForm" class="ap-form" @submit.prevent="saveFact">
            <h3>{{ editFactId ? 'ویرایش فکت' : 'فکت جدید' }}</h3>
            <label>عنوان *<input v-model="factForm.title" required @input="autoSlug"></label>
            <div class="ap-grid2">
              <label>اسلاگ (انگلیسی/فارسی، یکتا)<input v-model="factForm.slug" required @input="slugTouched = true"></label>
              <label>دسته<input v-model="factForm.category" placeholder="رکورد / عجیب / آمار…"></label>
            </div>
            <label>متن فکت — پاراگراف‌ها را با یک خط خالی جدا کنید *<textarea v-model="factForm.paragraphs" rows="7" required></textarea></label>
            <div class="ap-grid2">
              <label>عدد کلیدی (اختیاری)<input v-model="factForm.keyFigure" placeholder="مثلاً ۱۰۹"></label>
              <label>برچسب عدد<input v-model="factForm.figureLabel" placeholder="مثلاً گل ملی"></label>
            </div>
            <label>برچسب‌ها (با ویرگول جدا کنید)<input v-model="factForm.tags" placeholder="جام جهانی, رکورد, …"></label>
            <label>یادداشت راستی‌آزمایی<textarea v-model="factForm.verifyNote" rows="3"></textarea></label>
            <div class="ap-grid2">
              <label>نام منبع<input v-model="factForm.sourceName" placeholder="FIFA.com — …"></label>
              <label>لینک منبع<input v-model="factForm.sourceUrl" placeholder="https://…" dir="ltr"></label>
            </div>

            <div class="ap-img-box">
              <b>عکس فکت</b>
              <div class="ap-img-upload">
                <input ref="factFile" type="file" accept="image/jpeg,image/png,image/webp" @change="onFactImagePicked">
                <span class="ap-muted">JPG / PNG / WebP — حداکثر ۴ مگابایت</span>
              </div>
              <p v-if="imgUploading" class="ap-muted">در حال آپلود…</p>
              <p v-if="imgError" class="ap-err">{{ imgError }}</p>
              <img v-if="factForm.imageUrl" :src="factForm.imageUrl" alt="پیش‌نمایش عکس فکت" class="ap-img-preview">
              <label v-if="factForm.imageUrl">متن جایگزین عکس (alt)<input v-model="factForm.imageAlt"></label>
            </div>

            <div class="ap-grid2">
              <label class="ap-check"><input type="checkbox" v-model="factForm.published"> منتشر شود (نمایش در سایت)</label>
              <label>ترتیب نمایش<input type="number" v-model.number="factForm.sortOrder" min="0"></label>
            </div>
            <label class="ap-check"><input type="checkbox" v-model="factForm.featuredToday"> ⭐ این فکت «دانستنیِ امروز» باشد (کارت بزرگ صفحهٔ دانستنی‌ها)</label>
            <div class="ap-form-actions">
              <button type="submit" class="ap-btn" :disabled="busy || imgUploading">ذخیره فکت</button>
              <button type="button" class="ap-btn ghost" @click="closeFactForm">انصراف</button>
            </div>
          </form>

          <div class="ap-list">
            <div v-for="f in factsList" :key="f.id" class="ap-row fact">
              <img v-if="f.imageUrl" :src="f.imageUrl" alt="" class="ap-thumb">
              <div v-else class="ap-thumb empty">💡</div>
              <div class="ap-row-main">
                <h4>{{ f.title }} <span :class="['ap-badge', f.published ? 'ok' : '']">{{ f.published ? 'منتشرشده' : 'پیش‌نویس' }}</span> <span v-if="isFeaturedToday(f)" class="ap-badge today">⭐ امروز</span></h4>
                <p class="ap-muted">{{ f.category || 'بدون دسته' }} • اسلاگ: {{ f.slug }}</p>
              </div>
              <div class="ap-row-actions">
                <button class="ap-mini" @click="openFactForm(f)">ویرایش</button>
                <button class="ap-mini danger" @click="removeFact(f)">حذف</button>
              </div>
            </div>
            <p v-if="!factsList.length" class="ap-empty">فکتی ثبت نشده — با «فکت جدید» اولین دانستنی را بسازید.</p>
          </div>
        </section>

        <!-- ============ رویدادهای ویژه ============ -->
        <section v-if="activeTab === 'events'">
          <div class="ap-sec-head">
            <h2>رویدادهای ویژه <small>({{ eventsList.length }} رویداد)</small></h2>
            <button class="ap-btn" @click="openEventForm()">＋ رویداد جدید</button>
          </div>

          <form v-if="showEventForm" class="ap-form" @submit.prevent="saveEvent">
            <h3>{{ editEventId ? 'ویرایش رویداد' : 'رویداد جدید' }}</h3>
            <div class="ap-grid2">
              <label>عنوان *<input v-model="eventForm.title" required></label>
              <label>اسلاگ * (انگلیسی)<input v-model="eventForm.slug" required dir="ltr"></label>
            </div>
            <label>زیرعنوان<input v-model="eventForm.subtitle"></label>
            <label>توضیحات<textarea v-model="eventForm.description" rows="4"></textarea></label>
            <div class="ap-grid2">
              <label>شروع<input type="datetime-local" v-model="eventForm.startDate"></label>
              <label>پایان<input type="datetime-local" v-model="eventForm.endDate"></label>
            </div>
            <div class="ap-grid2">
              <label class="ap-check"><input type="checkbox" v-model="eventForm.isActive"> فعال</label>
              <label>ترتیب<input type="number" v-model.number="eventForm.sortOrder" min="0"></label>
            </div>
            <div class="ap-form-actions">
              <button type="submit" class="ap-btn" :disabled="busy">ذخیره</button>
              <button type="button" class="ap-btn ghost" @click="closeEventForm">انصراف</button>
            </div>
          </form>

          <div class="ap-list">
            <div v-for="e in eventsList" :key="e.id" class="ap-row">
              <div class="ap-row-main">
                <h4>{{ e.title }} <span :class="['ap-badge', e.isActive ? 'ok' : '']">{{ e.isActive ? 'فعال' : 'غیرفعال' }}</span></h4>
                <p class="ap-muted">{{ e.subtitle || '—' }} • اسلاگ: {{ e.slug }}</p>
              </div>
              <div class="ap-row-actions">
                <button class="ap-mini" @click="openEventForm(e)">ویرایش</button>
                <button class="ap-mini danger" @click="removeEvent(e)">حذف</button>
              </div>
            </div>
            <p v-if="!eventsList.length" class="ap-empty">رویدادی ثبت نشده است.</p>
          </div>
        </section>

        <!-- ============ سینک و مانیتورینگ ============ -->
        <section v-if="activeTab === 'sync'">
          <div class="ap-sec-head">
            <h2>سینک فوتبال و کووتا</h2>
            <button class="ap-btn ghost" @click="loadSync">بروزرسانی</button>
          </div>

          <div v-if="syncInfo.quota" class="ap-cards small">
            <div class="ap-card"><b>{{ syncInfo.quota.usedLastMinute }}/{{ syncInfo.quota.maxPerMinute }}</b><span>مصرف این دقیقه</span></div>
            <div class="ap-card"><b>{{ syncInfo.quota.total429 || 0 }}</b><span>خطای 429 کل</span></div>
            <div class="ap-card"><b>{{ syncInfo.quota.cooldownActive ? (syncInfo.quota.cooldownRemainsSec + 'ث') : 'خیر' }}</b><span>کول‌داون</span></div>
          </div>

          <div class="ap-list">
            <div v-for="s in syncStates" :key="s.key" class="ap-row">
              <div class="ap-row-main">
                <h4>{{ s.key }} <span :class="['ap-badge', stateClass(s.lastStatus)]">{{ faState(s.lastStatus) }}</span></h4>
                <p class="ap-muted">
                  آخرین پایان: {{ s.lastFinishedAt ? fmtDate(s.lastFinishedAt) : '—' }}
                  <template v-if="s.lastError"> • خطا: {{ String(s.lastError).slice(0, 90) }}</template>
                </p>
              </div>
              <div class="ap-row-actions">
                <button class="ap-mini" @click="triggerSync(s.key)">اجرای دستی</button>
              </div>
            </div>
            <p v-if="!syncStates.length" class="ap-empty">هنوز سینکی اجرا نشده است.</p>
          </div>
        </section>
      </main>
    </template>
  </div>
</template>

<script>
import api from '@/services/api'

const SYNC_JOBS = ['livePulse', 'standings', 'tableRecompute', 'windowSync', 'fullSeason', 'baseData', 'squad', 'playerV3', 'wikiPlayer', 'national', 'irLeague', 'irLeaguePulse']

export default {
  name: 'AdminPanel',

  data() {
    return {
      authed: false,
      tokenInput: '',
      loginBusy: false,
      loginError: '',
      loginNoServerToken: false,
      activeTab: 'dashboard',
      tabs: [
        { id: 'dashboard', name: 'داشبورد', icon: '📊' },
        { id: 'news', name: 'اخبار', icon: '📰' },
        { id: 'facts', name: 'دانستنی‌ها', icon: '💡' },
        { id: 'events', name: 'رویدادهای ویژه', icon: '🏆' },
        { id: 'sync', name: 'سینک و کووتا', icon: '🔄' },
      ],
      stats: {},
      busy: false,
      flash: '',
      flashType: 'ok',
      // اخبار
      newsList: [],
      showNewsForm: false,
      editNewsId: null,
      newsForm: { title: '', summary: '', content: '', imageUrl: '' },
      // دانستنی‌ها
      factsList: [],
      showFactForm: false,
      editFactId: null,
      slugTouched: false,
      imgUploading: false,
      imgError: '',
      factForm: this.blankFactForm(),
      // «دانستنیِ روز»
      dailyStatus: null,
      dailyBusy: false,
      dailyResult: '',
      // رویدادها
      eventsList: [],
      showEventForm: false,
      editEventId: null,
      eventForm: this.blankEventForm(),
      // سینک
      syncInfo: {},
    }
  },

  computed: {
    syncStates() {
      const list = Array.isArray(this.syncInfo.syncStates) ? this.syncInfo.syncStates : []
      const known = new Set(list.map((s) => s.key))
      const missing = SYNC_JOBS.filter((j) => !known.has(j)).map((k) => ({ key: k, lastStatus: null }))
      return [...list, ...missing]
    },
  },

    watch: {
    activeTab(tab) {
      if (tab === 'dashboard') this.loadStats()
      if (tab === 'news') this.loadNews()
      if (tab === 'facts') { this.loadFacts(); this.loadDaily() }
      if (tab === 'events') this.loadEvents()
      if (tab === 'sync') this.loadSync()
    },
  },

  mounted() {
    // توکن ذخیره‌شده از قبل؟ مستقیم بررسی اعتبار
    if (api.getAdminToken()) this.checkToken()
  },

  methods: {
    blankFactForm() {
      return {
        title: '', slug: '', category: '', paragraphs: '', tags: '',
        keyFigure: '', figureLabel: '', verifyNote: '',
        sourceName: '', sourceUrl: '', imageUrl: '', imageAlt: '',
        published: true, sortOrder: 0, featuredToday: false,
      }
    },
    blankEventForm() {
      return { title: '', slug: '', subtitle: '', description: '', startDate: '', endDate: '', isActive: false, sortOrder: 0 }
    },

    say(msg, type = 'ok') {
      this.flash = msg
      this.flashType = type
      clearTimeout(this._ft)
      this._ft = setTimeout(() => { this.flash = '' }, 4000)
    },

    // ---------- ورود ----------
    // خطاهای نشست ادمین — 401/503 یعنی باید از پنل بیرون بیفتد تا توکن تازه وارد شود
    adminFail(e, msg) {
      const st = e && e.status
      if (st === 401) {
        this.authed = false
        api.setAdminToken('')
        this.loginError = 'توکن نامعتبر یا منقضی است — دوباره وارد کنید.'
      } else if (st === 503) {
        this.authed = false
        api.setAdminToken('')
        this.loginNoServerToken = true
      } else {
        this.say(msg + (e && e.status ? ' (' + e.status + ')' : ''), 'err')
      }
    },
    async checkToken() {
      this.loginBusy = true
      this.loginError = ''
      this.loginNoServerToken = false
      try {
        await api.adminGetStats()
        this.authed = true
        this.loadStats()
      } catch (e) {
        const st = e && e.status
        if (st === 503) this.loginNoServerToken = true
        else if (st === 401) this.loginError = 'توکن نامعتبر است — دوباره وارد کنید.'
        else this.loginError = 'خطای ارتباط با سرور (' + (st || 0) + ')'
        api.setAdminToken('')
      } finally {
        this.loginBusy = false
      }
    },
    async login() {
      api.setAdminToken(this.tokenInput.trim())
      await this.checkToken()
      if (this.authed) this.tokenInput = ''
    },
    logout() {
      api.setAdminToken('')
      this.authed = false
    },

    // ---------- بارگذاری ----------
    async loadStats() {
      try { this.stats = await api.adminGetStats() || {} } catch (e) { this.adminFail(e, 'خطا در دریافت آمار') }
    },
    async loadNews() {
      try { this.newsList = (await api.adminGetNews()) || [] } catch (e) { this.adminFail(e, 'خطا در دریافت اخبار') }
    },
    async loadFacts() {
      try { this.factsList = (await api.adminGetFacts()) || [] } catch (e) { this.adminFail(e, 'خطا در دریافت فکت‌ها') }
    },
    async loadEvents() {
      try { this.eventsList = (await api.adminGetEvents()) || [] } catch (e) { this.adminFail(e, 'خطا در دریافت رویدادها') }
    },
    async loadSync() {
      try { this.syncInfo = (await api.adminGetSyncStatus()) || {} } catch (e) { this.adminFail(e, 'خطا در دریافت وضعیت سینک') }
    },

    // ---------- اخبار ----------
    openNewsForm(n) {
      this.showNewsForm = true
      if (n) {
        this.editNewsId = n.id
        this.newsForm = {
          title: n.title || '',
          summary: n.summary || '',
          content: '', // متن کامل در لیست نمی‌آید؛ خالی = دست‌نخورده
          imageUrl: n.imageUrl || '',
        }
      } else {
        this.editNewsId = null
        this.newsForm = { title: '', summary: '', content: '', imageUrl: '' }
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    closeNewsForm() { this.showNewsForm = false; this.editNewsId = null },
    async saveNews() {
      this.busy = true
      try {
        if (this.editNewsId) {
          const data = { ...this.newsForm }
          if (!data.content) delete data.content // متن خالی = حذف نشود
          await api.adminUpdateNews(this.editNewsId, data)
          this.say('خبر ویرایش شد')
        } else {
          await api.adminCreateNews(this.newsForm)
          this.say('خبر اضافه شد')
        }
        this.closeNewsForm()
        this.loadNews()
      } catch (e) {
        this.say('خطا در ذخیره خبر: ' + (e.message || ''), 'err')
      } finally { this.busy = false }
    },
    async removeNews(n) {
      if (!confirm('حذف خبر «' + n.title + '»؟')) return
      try {
        await api.adminDeleteNews(n.id)
        this.say('خبر حذف شد')
        this.loadNews()
      } catch { this.say('خطا در حذف خبر', 'err') }
    },

    // ---------- دانستنی‌ها ----------
    autoSlug() {
      if (this.slugTouched || this.editFactId) return
      this.factForm.slug = String(this.factForm.title || '')
        .trim()
        .replace(/[\s\u200c]+/g, '-')
        .replace(/[^\p{L}\p{N}-]+/gu, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 120)
    },
    openFactForm(f) {
      this.showFactForm = true
      this.imgError = ''
      if (f) {
        this.editFactId = f.id
        this.slugTouched = true
        this.factForm = {
          title: f.title || '',
          slug: f.slug || '',
          category: f.category || '',
          paragraphs: (f.paragraphs || []).join('\n\n'),
          tags: (f.tags || []).join('، '),
          keyFigure: f.keyFigure || '',
          figureLabel: f.figureLabel || '',
          verifyNote: f.verifyNote || '',
          sourceName: (f.source && f.source.name) || '',
          sourceUrl: (f.source && f.source.url) || '',
          imageUrl: f.imageUrl || '',
          imageAlt: f.imageAlt || '',
          published: !!f.published,
          sortOrder: f.sortOrder || 0,
          featuredToday: this.isFeaturedToday(f),
        }
      } else {
        this.editFactId = null
        this.slugTouched = false
        this.factForm = this.blankFactForm()
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    closeFactForm() { this.showFactForm = false; this.editFactId = null; this.imgError = '' },
    async onFactImagePicked(ev) {
      const file = ev.target.files && ev.target.files[0]
      if (!file) return
      this.imgError = ''
      if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
        this.imgError = 'فقط JPG / PNG / WebP مجاز است'
        return
      }
      if (file.size > 4 * 1024 * 1024) {
        this.imgError = 'حجم عکس بیش از ۴ مگابایت است'
        return
      }
      this.imgUploading = true
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader()
          r.onload = () => resolve(r.result)
          r.onerror = () => reject(new Error('خواندن فایل ناموفق بود'))
          r.readAsDataURL(file)
        })
        const res = await api.adminUploadFactImage(dataUrl)
        if (res && res.url) {
          this.factForm.imageUrl = res.url
          if (!this.factForm.imageAlt) this.factForm.imageAlt = this.factForm.title || ''
          this.say('عکس آپلود شد')
        } else {
          this.imgError = (res && res.error) || 'آپلود ناموفق بود'
        }
      } catch (e) {
        this.imgError = 'خطا در آپلود: ' + (e.message || '')
      } finally {
        this.imgUploading = false
        if (this.$refs.factFile) this.$refs.factFile.value = ''
      }
    },
    async saveFact() {
      this.busy = true
      try {
        const payload = { ...this.factForm }
        if (this.editFactId) {
          await api.adminUpdateFact(this.editFactId, payload)
          this.say('فکت ویرایش شد — پیش‌رندر ساعتی صفحهٔ دانستنی‌ها را بروز می‌کند')
        } else {
          await api.adminCreateFact(payload)
          this.say('فکت ساخته شد')
        }
        this.closeFactForm()
        this.loadFacts()
      } catch (e) {
        this.say('خطا در ذخیره فکت: ' + (e.message || ''), 'err')
      } finally { this.busy = false }
    },
    async removeFact(f) {
      if (!confirm('حذف فکت «' + f.title + '»؟')) return
      try {
        await api.adminDeleteFact(f.id)
        this.say('فکت حذف شد')
        this.loadFacts()
        this.loadDaily()
      } catch { this.say('خطا در حذف فکت', 'err') }
    },

    // ---------- «دانستنیِ روز» ----------
    async loadDaily() {
      try { this.dailyStatus = await api.adminDailyStatus() } catch { this.dailyStatus = null }
    },
    async runDaily(force = false) {
      if (force && !confirm('دانستنیِ فعلی امروز به بایگانی می‌رود و مطلب تازه‌ای از منابع ساخته و منتشر می‌شود. ادامه؟')) return
      this.dailyBusy = true
      this.dailyResult = ''
      try {
        const r = await api.adminDailyRun({ force })
        if (r && r.created) {
          this.say('✅ دانستنیِ امروز ساخته و منتشر شد: ' + (r.fact && r.fact.title || ''))
          this.dailyResult = 'منتشر شد: ' + (r.fact && r.fact.title || '')
            + '\nعکس: ' + (r.imageProvider || '-')
            + '\nآدرس: /did-you-know/' + (r.fact && r.fact.slug || '')
        } else if (r && r.skipped) {
          this.say('امروز قبلاً منتشر شده: ' + (r.fact && r.fact.title || ''))
          this.dailyResult = 'skip: قبلاً منتشر شده — با «جایگزینی با مطلب تازه» عوضش کنید'
        } else {
          this.say('امروز مطلبی ساخته نشد (' + ((r && r.reason) || '?') + ')', 'err')
          this.dailyResult = 'reason: ' + ((r && r.reason) || '?')
        }
        this.loadFacts()
        this.loadDaily()
      } catch (e) {
        this.say('خطا در اجرای خط لوله: ' + (e.message || ''), 'err')
        this.dailyResult = 'error: ' + (e.message || '')
      } finally { this.dailyBusy = false }
    },
    async previewDaily() {
      this.dailyBusy = true
      this.dailyResult = ''
      try {
        const r = await api.adminDailyRun({ dryRun: true })
        if (r && r.wouldPublish) {
          const w = r.wouldPublish
          this.dailyResult = 'کاندید نهایی (منتشر نشد):\n'
            + 'عنوان: ' + w.title + '\n'
            + 'منبع: ' + w.sourceName + '\n'
            + 'دسته: ' + (w.category || '-') + ' | عدد کلیدی: ' + (w.keyFigure || '-') + '\n'
            + 'عکس: ' + (w.imageUrl || 'هیچ') + '\n'
            + 'پاراگراف‌ها: ' + (w.paragraphs || []).length
          this.say('پیش‌نمایش آماده است — کاندید در جعبهٔ زیر')
        } else {
          this.dailyResult = 'کاندیدی بالا نیامد: ' + ((r && r.reason) || '?')
          this.say('کاندیدی پیدا نشد — بعداً دوباره امتحان کنید', 'err')
        }
      } catch (e) {
        this.say('خطا در پیش‌نمایش: ' + (e.message || ''), 'err')
        this.dailyResult = 'error: ' + (e.message || '')
      } finally { this.dailyBusy = false }
    },
    isFeaturedToday(f) {
      if (!f || !f.featuredAt) return false
      try {
        const day = (d) => new Date(d).toLocaleDateString('en-CA', { timeZone: 'Asia/Tehran' })
        return day(f.featuredAt) === day(new Date())
      } catch { return false }
    },

    // ---------- رویدادها ----------
    openEventForm(e) {
      this.showEventForm = true
      if (e) {
        this.editEventId = e.id
        this.eventForm = {
          title: e.title || '',
          slug: e.slug || '',
          subtitle: e.subtitle || '',
          description: e.description || '',
          startDate: this.toLocalInput(e.startDate),
          endDate: this.toLocalInput(e.endDate),
          isActive: !!e.isActive,
          sortOrder: e.sortOrder || 0,
        }
      } else {
        this.editEventId = null
        this.eventForm = this.blankEventForm()
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    closeEventForm() { this.showEventForm = false; this.editEventId = null },
    async saveEvent() {
      this.busy = true
      try {
        const payload = { ...this.eventForm }
        if (this.editEventId) {
          await api.adminUpdateEvent(this.editEventId, payload)
          this.say('رویداد ویرایش شد')
        } else {
          await api.adminCreateEvent(payload)
          this.say('رویداد ساخته شد')
        }
        this.closeEventForm()
        this.loadEvents()
      } catch (e) {
        this.say('خطا در ذخیره رویداد: ' + (e.message || ''), 'err')
      } finally { this.busy = false }
    },
    async removeEvent(e) {
      if (!confirm('حذف رویداد «' + e.title + '»؟')) return
      try {
        await api.adminDeleteEvent(e.id)
        this.say('رویداد حذف شد')
        this.loadEvents()
      } catch { this.say('خطا در حذف رویداد', 'err') }
    },

    // ---------- سینک ----------
    async triggerSync(job) {
      try {
        await api.adminTriggerSync(job)
        this.say('اجرای «' + job + '» در صف قرار گرفت')
        setTimeout(this.loadSync, 4000)
      } catch { this.say('خطا در اجرای دستی', 'err') }
    },
    faState(st) {
      if (!st) return 'هرگز'
      const s = String(st).toUpperCase()
      if (s === 'SUCCESS' || s === 'OK') return 'موفق'
      if (s === 'ERROR' || s === 'FAILED') return 'خطا'
      if (s === 'RUNNING') return 'در حال اجرا'
      if (s === 'SKIPPED') return 'ردشده'
      return s
    },
    stateClass(st) {
      const s = String(st || '').toUpperCase()
      if (s === 'SUCCESS' || s === 'OK') return 'ok'
      if (s === 'ERROR' || s === 'FAILED') return 'bad'
      return ''
    },

    // ---------- ابزار ----------
    fmtDate(d) {
      if (!d) return '—'
      try { return new Date(d).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' }) } catch { return '—' }
    },
    toLocalInput(d) {
      if (!d) return ''
      try {
        const dt = new Date(d)
        const p = (n) => String(n).padStart(2, '0')
        return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`
      } catch { return '' }
    },
  },
}
</script>

<style scoped>
.ap { min-height: 100vh; background: #f1f5f9; color: #0f172a; font-family: inherit; }
.ap-login-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: linear-gradient(160deg, #0f172a, #1e3a5f); padding: 16px; }
.ap-login { background: #fff; border-radius: 18px; padding: 34px 30px; width: 100%; max-width: 400px; text-align: center; box-shadow: 0 24px 60px rgba(0,0,0,.35); }
.ap-login-icon { font-size: 40px; margin-bottom: 6px; }
.ap-login h1 { margin: 0 0 8px; font-size: 19px; }
.ap-login-hint { color: #64748b; font-size: 13px; line-height: 1.9; margin: 0 0 16px; }
.ap-login input { width: 100%; box-sizing: border-box; border: 1.5px solid #cbd5e1; border-radius: 10px; padding: 11px 13px; font-size: 14px; margin-bottom: 12px; }
.ap-login button { width: 100%; background: #16a34a; border: 0; color: #fff; font-weight: 800; font-size: 15px; border-radius: 10px; padding: 12px; cursor: pointer; }
.ap-login button:disabled { opacity: .6; }
.ap-err { color: #dc2626; font-size: 13px; margin: 8px 0 0; }
.ap-warn { color: #b45309; font-size: 12.5px; line-height: 1.9; margin: 8px 0 0; }

.ap-head { background: #0f172a; color: #fff; }
.ap-head-in { max-width: 1080px; margin: 0 auto; padding: 16px 16px 0; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.ap-head h1 { margin: 0; font-size: 18px; }
.ap-head h1 span { color: #4ade80; }
.ap-head-actions { display: flex; gap: 8px; }
.ap-ghost { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.2); color: #fff; border-radius: 8px; padding: 7px 14px; font-size: 13px; cursor: pointer; text-decoration: none; }
.ap-ghost:hover { background: rgba(255,255,255,.16); }
.ap-tabs { max-width: 1080px; margin: 0 auto; padding: 12px 16px 0; display: flex; gap: 6px; overflow-x: auto; }
.ap-tab { background: transparent; border: 0; border-bottom: 3px solid transparent; color: #94a3b8; font-size: 14px; font-weight: 700; padding: 10px 14px; cursor: pointer; white-space: nowrap; }
.ap-tab.on { color: #fff; border-bottom-color: #4ade80; }

.ap-body { max-width: 1080px; margin: 0 auto; padding: 18px 16px 60px; }
.ap-flash { border-radius: 12px; padding: 11px 16px; font-size: 14px; font-weight: 700; margin-bottom: 14px; }
.ap-flash.ok { background: #dcfce7; color: #166534; }
.ap-flash.err { background: #fee2e2; color: #991b1b; }

.ap-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
.ap-cards.small .ap-card { padding: 14px; }
.ap-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 18px 14px; text-align: center; box-shadow: 0 1px 3px rgba(15,23,42,.06); }
.ap-card b { display: block; font-size: 26px; color: #166534; }
.ap-card span { color: #64748b; font-size: 12.5px; font-weight: 600; }
.ap-note { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 16px 18px; margin-top: 14px; font-size: 14px; line-height: 2; }
.ap-note a { color: #16a34a; font-weight: 700; }

.ap-sec-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin: 6px 0 14px; }
.ap-sec-head h2 { margin: 0; font-size: 17px; }
.ap-sec-head small { color: #64748b; font-weight: 400; }
.ap-btn { background: #16a34a; color: #fff; border: 0; border-radius: 10px; padding: 10px 18px; font-weight: 800; font-size: 14px; cursor: pointer; }
.ap-btn.ghost { background: #fff; color: #0f172a; border: 1.5px solid #cbd5e1; }
.ap-btn:disabled { opacity: .55; cursor: default; }

.ap-form { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 18px 20px; margin-bottom: 16px; }
.ap-form h3 { margin: 0 0 12px; font-size: 15px; }
.ap-form label { display: block; font-size: 13px; font-weight: 700; color: #334155; margin-bottom: 10px; }
.ap-form input:not([type=checkbox]):not([type=file]), .ap-form textarea { width: 100%; box-sizing: border-box; margin-top: 5px; border: 1.5px solid #cbd5e1; border-radius: 9px; padding: 9px 12px; font-size: 14px; font-family: inherit; }
.ap-form textarea { resize: vertical; line-height: 1.9; }
.ap-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; }
.ap-check { display: flex; align-items: center; gap: 8px; padding-top: 20px; }
.ap-form-actions { display: flex; gap: 10px; margin-top: 6px; }

.ap-img-box { background: #f8fafc; border: 1.5px dashed #cbd5e1; border-radius: 12px; padding: 14px 16px; margin: 6px 0 12px; }
.ap-img-box > b { font-size: 13.5px; }
.ap-img-upload { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin: 8px 0; }
.ap-img-preview { display: block; max-width: 320px; max-height: 190px; border-radius: 12px; border: 1px solid #e2e8f0; margin-top: 10px; }

.ap-list { display: flex; flex-direction: column; gap: 10px; }
.ap-row { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 13px 16px; display: flex; align-items: center; gap: 12px; }
.ap-row-main { flex: 1; min-width: 0; }
.ap-row-main h4 { margin: 0 0 4px; font-size: 14.5px; }
.ap-row-actions { display: flex; gap: 6px; flex-shrink: 0; align-items: center; }
.ap-mini { background: #f1f5f9; border: 1px solid #e2e8f0; color: #0f172a; border-radius: 8px; padding: 6px 12px; font-size: 12.5px; font-weight: 700; cursor: pointer; text-decoration: none; }
.ap-mini:hover { background: #e2e8f0; }
.ap-mini.danger { color: #b91c1c; }
.ap-mini.danger:hover { background: #fee2e2; }
.ap-muted { color: #64748b; font-size: 12.5px; margin: 0; }
.ap-empty { color: #94a3b8; text-align: center; padding: 22px; background: #fff; border: 1px dashed #e2e8f0; border-radius: 14px; }
.ap-badge { display: inline-block; background: #f1f5f9; color: #475569; border-radius: 999px; font-size: 11px; font-weight: 800; padding: 3px 10px; vertical-align: middle; }
.ap-badge.ok { background: #dcfce7; color: #166534; }
.ap-badge.bad { background: #fee2e2; color: #991b1b; }
.ap-badge.today { background: #fef3c7; color: #92400e; }

/* «دانستنیِ روز» */
.ap-daily { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; margin-bottom: 18px; }
.ap-daily-status { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; font-size: 13.5px; margin-bottom: 10px; }
.ap-daily-status a { color: #047857; font-weight: 700; text-decoration: none; }
.ap-daily-status a:hover { text-decoration: underline; }
.ap-daily-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.ap-mini.ghost { background: #fff; }
.ap-daily-result { background: #0f172a; color: #a7f3d0; border-radius: 10px; padding: 12px 14px; font-size: 12.5px; line-height: 1.9; overflow-x: auto; white-space: pre-wrap; margin: 8px 0; }
.ap-daily-hint { font-size: 12px; line-height: 1.9; margin: 6px 0 0; }
.ap-thumb { width: 72px; height: 54px; object-fit: cover; border-radius: 10px; border: 1px solid #e2e8f0; flex-shrink: 0; }
.ap-thumb.empty { display: flex; align-items: center; justify-content: center; background: #f8fafc; font-size: 22px; }

@media (max-width: 640px) {
  .ap-grid2 { grid-template-columns: 1fr; }
  .ap-check { padding-top: 0; }
  .ap-row { flex-wrap: wrap; }
}
</style>
